import * as React from 'react';
import { useRef, useState, useEffect } from 'react';
import { ColumnDefinition } from '../../helpers/table/table';
import './ColumnToggleButton.scss';

interface Props {
  columns: ColumnDefinition[];
  hiddenFields: Set<string>;
  onToggle: (field: string) => void;
  isToggleable: (field: string) => boolean;
  onReset?: () => void;
}

export const ColumnToggleButton: React.FunctionComponent<Props> = ({
  columns,
  hiddenFields,
  onToggle,
  isToggleable,
  onReset
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    const keyHandler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setIsOpen(false);
    };
    document.addEventListener('mousedown', handler);
    document.addEventListener('keydown', keyHandler);
    return () => {
      document.removeEventListener('mousedown', handler);
      document.removeEventListener('keydown', keyHandler);
    };
  }, [isOpen]);

  if (columns.length === 0) return null;

  return (
    <div className="columnToggle" ref={containerRef}>
      <button
        type="button"
        className="columnToggle__btn"
        onClick={() => setIsOpen(v => !v)}
        title="Show/hide columns"
        aria-expanded={isOpen}
      >
        <svg
          width="15"
          height="15"
          viewBox="0 0 15 15"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          aria-hidden="true"
        >
          <path d="M1 3.5H14M1 7.5H14M1 11.5H14" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
          <circle cx="4" cy="3.5" r="1.5" fill="currentColor" />
          <circle cx="8" cy="7.5" r="1.5" fill="currentColor" />
          <circle cx="11" cy="11.5" r="1.5" fill="currentColor" />
        </svg>
        <span className="columnToggle__label">Columns</span>
        <b>{columns.filter(column => !hiddenFields.has(column.field)).length}</b>
      </button>

      {isOpen && (
        <div className="columnToggle__dropdown">
          <div className="columnToggle__dropdownTitle">
            <span>Show / hide columns</span>
            {onReset ? <button type="button" onClick={onReset}>Reset</button> : null}
          </div>
          {columns.map(col => {
            const isVisible = !hiddenFields.has(col.field);
            const canToggle = isToggleable(col.field);
            return (
              <label key={col.field} className={`columnToggle__item${canToggle ? '' : ' columnToggle__item--locked'}`}>
                <input
                  type="checkbox"
                  className="columnToggle__checkbox"
                  checked={isVisible}
                  disabled={!canToggle}
                  onChange={() => onToggle(col.field)}
                />
                <span className="columnToggle__itemText">{col.text}</span>
              </label>
            );
          })}
        </div>
      )}
    </div>
  );
};

ColumnToggleButton.displayName = 'ColumnToggleButton';
